import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, TYPE_LABELS, MODE_LABELS, modeBadge, scoreColor, levelColor } from "@/lib/api";
import { toast } from "sonner";
import { Flame, Loader2, Rocket, TrendingUp, Trophy, Target, DollarSign, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

const Stat = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-white border border-zinc-200 rounded-lg p-4 flex items-center gap-3">
    <div className={`w-9 h-9 rounded-md flex items-center justify-center ${tint}`}><Icon className="w-4.5 h-4.5" /></div>
    <div>
      <div className="font-display text-2xl font-bold leading-none">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{label}</div>
    </div>
  </div>
);

export default function Opportunities() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [hotOnly, setHotOnly] = useState(false);
  const [minScore, setMinScore] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get(`/opportunities?only_unbuilt=true&hot_only=${hotOnly}&min_score=${minScore}`)
      .then(({ data }) => setData(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [hotOnly, minScore]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const build = async (ids) => {
    if (!ids.length) return;
    try {
      const { data: r } = await api.post("/products/bulk-build", { idea_ids: ids, queue_social: false });
      toast.success(`Building ${ids.length} package${ids.length > 1 ? "s" : ""} in the background…`);
      setSelected(new Set());
      setJob({ id: r.job_id, status: "running", completed: 0, total: ids.length });
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${r.job_id}`);
          setJob(j);
          if (["done", "error", "stopped"].includes(j.status)) {
            clearInterval(pollRef.current);
            if (j.status === "done") toast.success(`Built ${j.result?.built || 0} products`);
            else if (j.status === "stopped") toast.warning("Stopped — cost limit reached");
            load();
          }
        } catch { clearInterval(pollRef.current); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Build failed");
    }
  };

  const buildTop = (n) => build(data.items.slice(0, n).map((i) => i.id));

  return (
    <div className="max-w-6xl mx-auto" data-testid="opportunities-page">
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-blue-600">Smart View</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <Flame className="w-7 h-7 text-orange-500" /> Top opportunities
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Unbuilt concepts ranked by opportunity — overall impulse weighted with low competition &amp; margin.</p>
        </div>
        <button onClick={() => buildTop(5)} disabled={!data.items.length} data-testid="build-top-5-btn"
          className="h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium flex items-center gap-2 transition-colors">
          <Rocket className="w-4 h-4" /> Build top 5
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-5">
        <Stat icon={Trophy} label="Ranked opportunities" value={data.stats?.total ?? 0} tint="bg-blue-100 text-blue-700" />
        <Stat icon={Flame} label="Hot combos (low comp + high margin)" value={data.stats?.hot ?? 0} tint="bg-orange-100 text-orange-700" />
        <Stat icon={TrendingUp} label="Avg impulse score" value={data.stats?.avg_score ?? 0} tint="bg-emerald-100 text-emerald-700" />
      </div>

      <div className="flex items-center gap-4 mb-3 flex-wrap">
        <label className="flex items-center gap-2 text-sm"><Switch checked={hotOnly} onCheckedChange={setHotOnly} data-testid="hot-only-toggle" /> Hot combos only</label>
        <label className="flex items-center gap-2 text-sm text-zinc-600">Min impulse
          <select value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} data-testid="min-score-select" className="bg-white border border-zinc-200 rounded-md px-2 py-1.5 text-sm">
            {[0, 50, 60, 70, 80].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        {selected.size > 0 && (
          <button onClick={() => build([...selected])} data-testid="build-selected-btn"
            className="ml-auto h-9 px-3 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-medium flex items-center gap-1.5">
            <Rocket className="w-3.5 h-3.5" /> Build {selected.size} selected
          </button>
        )}
      </div>

      {job && job.status === "running" && (
        <div className="bg-white border border-blue-200 rounded-lg p-3 mb-3 flex items-center gap-3" data-testid="opp-job">
          <Loader2 className="w-4 h-4 animate-spin text-blue-600" /><span className="text-sm font-medium">Building…</span>
          <span className="text-xs text-zinc-500 ml-auto">{job.completed}/{job.total}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : data.items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-lg p-16 text-center">
          <Sparkles className="w-8 h-8 text-zinc-300 mx-auto" />
          <p className="text-sm text-zinc-500 mt-3">No unbuilt concepts match. Generate more in the Idea Engine or lower the filters.</p>
          <button onClick={() => navigate("/ideas")} className="text-sm text-blue-600 mt-2">Go to Idea Engine →</button>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          {data.items.map((i, idx) => (
            <div key={i.id} data-testid={`opp-row-${i.id}`} className="flex items-center gap-3 px-4 py-3 border-b border-zinc-100 last:border-0 hover:bg-zinc-50 transition-colors">
              <Checkbox checked={selected.has(i.id)} onCheckedChange={() => toggle(i.id)} data-testid={`opp-select-${i.id}`} />
              <div className="w-7 text-center font-display font-bold text-zinc-300 tabular-nums">{idx + 1}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-semibold truncate">{i.title}</span>
                  {i.hot && <Badge className="text-[9px] bg-orange-100 text-orange-700 hover:bg-orange-100"><Flame className="w-2.5 h-2.5 mr-0.5" />Hot</Badge>}
                  <Badge variant="secondary" className="text-[9px]">{TYPE_LABELS[i.product_type]}</Badge>
                  {i.mode && <Badge className={`text-[9px] border ${modeBadge[i.mode]} hover:opacity-100`}>{MODE_LABELS[i.mode]}</Badge>}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[11px] text-zinc-400">
                  <span>{i.theme_name}</span>
                  <span className="flex items-center gap-0.5"><Target className="w-3 h-3" /><span className={levelColor(i.competition)}>{i.competition} comp</span></span>
                  <span className="flex items-center gap-0.5"><DollarSign className="w-3 h-3" /><span className={levelColor(i.margin)}>{i.margin} margin</span></span>
                </div>
              </div>
              <div className="text-right shrink-0 w-16">
                <div className="font-display text-xl font-bold leading-none text-blue-600">{i.opportunity_score}</div>
                <div className="text-[9px] text-zinc-400 uppercase tracking-wide">opp score</div>
              </div>
              <div className={`text-right shrink-0 w-12 font-bold ${scoreColor(i.impulse_score)}`} title="Impulse score">★{i.impulse_score}</div>
              <button onClick={() => build([i.id])} data-testid={`opp-build-${i.id}`} className="h-8 px-3 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-medium flex items-center gap-1.5 shrink-0">
                <Rocket className="w-3.5 h-3.5" /> Build
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
